// frontend/src/pages/SchoolProjects/TechnicianParts/ReturnParts.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

const ReturnParts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Search state
  const [searchParams, setSearchParams] = useState({
    technician_id: '',
    part_id: '',
    status: 'issued'
  });
  
  // Results state
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  
  // Return form state
  const [returnForm, setReturnForm] = useState({
    assignment_id: '',
    return_quantity: '',
    condition: 'good',
    notes: ''
  });
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Data lists
  const [technicians, setTechnicians] = useState([]);
  const [parts, setParts] = useState([]);

  useEffect(() => {
    loadTechnicians();
  }, []);

  const loadTechnicians = async () => {
    try {
      const response = await api.getTechniciansList();
      setTechnicians(response.data || []);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const searchAssignments = async () => {
    if (!searchParams.technician_id) {
      setMessage({ type: 'error', text: 'Please select a technician to search' });
      return;
    }

    setSearching(true);
    try {
      const params = {
        technician_id: searchParams.technician_id,
        status: searchParams.status
      };
      
      if (searchParams.part_id) {
        params.part_id = searchParams.part_id;
      }

      const response = await api.getTechnicianAssignments(params);
      const filtered = (response.data || []).filter(a => 
        a.status === 'issued' || a.status === 'overdue' || a.status === 'partially_returned'
      );
      
      setAssignments(filtered);
      
      if (filtered.length === 0) {
        setMessage({ type: 'info', text: 'No active assignments found for this technician' });
      }
    } catch (error) {
      console.error('Error searching assignments:', error);
      setMessage({ type: 'error', text: 'Failed to search assignments. Please try again.' });
    } finally {
      setSearching(false);
    }
  };

  const loadParts = async (technicianId) => {
    try {
      const response = await api.getTechnicianAssignmentHistory(technicianId);
      const partIds = [...new Set(response.data.map(a => a.part_id))];
      
      // Get part details for these IDs
      const partsList = await api.getComputerParts();
      const filteredParts = partsList.data.filter(p => 
        partIds.includes(p.id)
      );
      
      setParts(filteredParts);
    } catch (error) {
      console.error('Error loading parts:', error);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'technician_id' && value) {
      loadParts(value);
    }
  };

  const handleReturnFormChange = (e) => {
    const { name, value } = e.target;
    setReturnForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openReturnDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setReturnForm({
      assignment_id: assignment.id,
      return_quantity: assignment.quantity,
      condition: 'good',
      notes: ''
    });
    setDialogOpen(true);
  };

  const closeReturnDialog = () => {
    setDialogOpen(false);
    setSelectedAssignment(null);
    setReturnForm({
      assignment_id: '',
      return_quantity: '',
      condition: 'good',
      notes: ''
    });
  };

  const submitReturn = async () => {
    if (!returnForm.return_quantity || returnForm.return_quantity <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid return quantity' });
      return;
    }

    if (returnForm.return_quantity > selectedAssignment.quantity) {
      setMessage({ 
        type: 'error', 
        text: `Return quantity cannot exceed issued quantity (${selectedAssignment.quantity})` 
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        assignment_id: returnForm.assignment_id,
        return_quantity: parseInt(returnForm.return_quantity),
        condition: returnForm.condition,
        notes: returnForm.notes
      };

      const response = await api.returnPartsFromTechnician(payload);
      
      setMessage({ 
        type: 'success', 
        text: `Successfully returned ${returnForm.return_quantity} item(s)` 
      });
      
      closeReturnDialog();
      searchAssignments(); // Refresh the list
      
    } catch (error) {
      console.error('Error returning parts:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to return parts. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'issued': return 'primary';
      case 'overdue': return 'error';
      case 'partially_returned': return 'warning';
      case 'returned': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'issued': return <CheckCircleIcon color="primary" />;
      case 'overdue': return <WarningIcon color="error" />;
      case 'partially_returned': return <WarningIcon color="warning" />;
      case 'returned': return <CheckCircleIcon color="success" />;
      default: return null;
    }
  };

  const isOverdue = (assignment) => {
    if (!assignment.expected_return_date) return false;
    const expectedDate = new Date(assignment.expected_return_date);
    const today = new Date();
    return today > expectedDate;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/school-projects/technician-parts')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom>
            Return Parts from Technician
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Process returns of computer parts from technicians
          </Typography>
        </Box>
      </Box>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Search Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Active Assignments
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select Technician</InputLabel>
              <Select
                name="technician_id"
                value={searchParams.technician_id}
                onChange={handleSearchChange}
                label="Select Technician"
              >
                {technicians.map(tech => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.name} - {tech.specialization || 'General Technician'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Part (Optional)</InputLabel>
              <Select
                name="part_id"
                value={searchParams.part_id}
                onChange={handleSearchChange}
                label="Filter by Part (Optional)"
                disabled={!searchParams.technician_id}
              >
                <MenuItem value="">All Parts</MenuItem>
                {parts.map(part => (
                  <MenuItem key={part.id} value={part.id}>
                    {part.part_name} ({part.part_number})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={searchParams.status}
                onChange={handleSearchChange}
                label="Status"
              >
                <MenuItem value="issued">Issued</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="partially_returned">Partially Returned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
              onClick={searchAssignments}
              disabled={searching || !searchParams.technician_id}
              fullWidth
            >
              {searching ? 'Searching...' : 'Search Assignments'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Section */}
      {assignments.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Active Assignments ({assignments.length})
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={searchAssignments} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          {searching ? (
            <LinearProgress />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Technician</TableCell>
                    <TableCell>Part</TableCell>
                    <TableCell>School</TableCell>
                    <TableCell>Issued Date</TableCell>
                    <TableCell>Expected Return</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((assignment) => (
                    <TableRow 
                      key={assignment.id}
                      sx={{ 
                        backgroundColor: isOverdue(assignment) ? 'error.light' : 'inherit',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        {assignment.Technician?.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {assignment.ComputerPart?.part_name || 'N/A'}
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                            ({assignment.ComputerPart?.part_number || 'N/A'})
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {assignment.School?.school_name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {new Date(assignment.assignment_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {assignment.expected_return_date ? (
                          <Chip 
                            label={new Date(assignment.expected_return_date).toLocaleDateString()}
                            size="small"
                            color={isOverdue(assignment) ? "error" : "default"}
                            variant={isOverdue(assignment) ? "filled" : "outlined"}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Not set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="medium">
                          {assignment.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(assignment.status)}
                          label={assignment.status.replace('_', ' ').toUpperCase()}
                          size="small"
                          color={getStatusColor(assignment.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/school-projects/technician-parts/assignments/${assignment.id}`)}
                            sx={{ mr: 1 }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => openReturnDialog(assignment)}
                          color="primary"
                        >
                          Return
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Return Dialog */}
      <Dialog open={dialogOpen} onClose={closeReturnDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Return Parts
        </DialogTitle>
        <DialogContent>
          {selectedAssignment && (
            <Box>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Assignment Details
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Technician:</strong> {selectedAssignment.Technician?.name || 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Part:</strong> {selectedAssignment.ComputerPart?.part_name || 'N/A'} 
                    ({selectedAssignment.ComputerPart?.part_number || 'N/A'})
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Issued Quantity:</strong> {selectedAssignment.quantity}
                  </Typography>
                  <Typography variant="body1">
                    <strong>School:</strong> {selectedAssignment.School?.school_name || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    name="return_quantity"
                    label="Return Quantity"
                    value={returnForm.return_quantity}
                    onChange={handleReturnFormChange}
                    inputProps={{ 
                      min: 1, 
                      max: selectedAssignment.quantity 
                    }}
                    helperText={`Maximum: ${selectedAssignment.quantity}`}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <Typography variant="subtitle2" gutterBottom>
                      Condition of Returned Parts
                    </Typography>
                    <RadioGroup
                      name="condition"
                      value={returnForm.condition}
                      onChange={handleReturnFormChange}
                    >
                      <FormControlLabel value="good" control={<Radio />} label="Good - Ready for reuse" />
                      <FormControlLabel value="damaged" control={<Radio />} label="Damaged - Needs repair" />
                      <FormControlLabel value="lost" control={<Radio />} label="Lost - Not returned" />
                      <FormControlLabel value="other" control={<Radio />} label="Other" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="notes"
                    label="Return Notes"
                    value={returnForm.notes}
                    onChange={handleReturnFormChange}
                    placeholder="Enter details about the return condition, issues found, etc."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReturnDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={submitReturn} 
            variant="contained" 
            disabled={loading || !returnForm.return_quantity}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : 'Confirm Return'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message.text}
        autoHideDuration={6000}
        onClose={() => setMessage({ type: '', text: '' })}
      >
        <Alert 
          severity={message.type}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReturnParts;